import { useInfiniteQuery } from '@tanstack/react-query'
import { useState } from 'react'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { getHoldingProductList } from '../../api/product'
import type { GetHoldingProductListParams } from '../../api/product'

import { CenterWrap } from '../../components/common'
import { Sort } from '../../components/paramOption/Sort'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'
import { Spinner } from '../../components/spinner/Spinner'
import { EmptyPage } from '../../components/exception/EmptyPage'
import { ProductCard } from './components/ProductCard'

const SORT_LIST: { type: GetHoldingProductListParams['sort']; title: string }[] = [
  {
    type: 'createdDate,desc',
    title: '최근 거래순',
  },
  {
    type: 'createdDate,asc',
    title: '과거 거래순',
  },
]

export function AssetHold() {
  const [sort, setSortType] = useState<GetHoldingProductListParams['sort']>('createdDate,desc')

  const { data, hasNextPage, fetchNextPage, isPending, isError } = useInfiniteQuery({
    queryKey: [QUERY_KEY.holdingProductList, { sort }],
    queryFn: async ({ pageParam = 0 }) => await getHoldingProductList({ sort, offset: pageParam, size: 50 }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })

  if (isPending || isError) {
    return (
      <CenterWrap>
        <Spinner />
      </CenterWrap>
    )
  }

  if (!data.pages.length) {
    return <EmptyPage content="조회 가능한 내역이 없습니다." />
  }

  return (
    <>
      <SortContainer>
        <Sort sortType={sort} sortList={SORT_LIST} setSortType={setSortType} />
      </SortContainer>
      <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
        <ProductsContainer>
          {data.pages.map((product) => (
            <ProductCard key={product.id} {...product} />
          ))}
        </ProductsContainer>
      </InfinityScroll>
    </>
  )
}

const ProductsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 15px;
  padding: 0 16px;
`

const SortContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`
